import { supabase } from "@/integrations/supabase/client";

export const LAST_STUDY_POINT_STORAGE_PREFIX = "mblearn.lastStudyPoint.v1";
export const LAST_STUDY_POINT_EVENT = "mblearn:last-study-point";

export type LastStudyPointMode = "quiz" | "study" | "exam" | "review" | "real-exam";

export interface LastStudyPoint {
  userId: string;
  mode: LastStudyPointMode;
  category: string | null;
  level: number | null;
  currentQuestion: number;
  questionId: string | null;
  route: string;
  questionVersion?: "full" | "short";
  sessionKey?: string | null;
  updatedAt: number;
}

const getStorageKey = (userId: string) => `${LAST_STUDY_POINT_STORAGE_PREFIX}.${userId}`;

const emitUpdate = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(LAST_STUDY_POINT_EVENT));
};

// Debounce remote writes per user to avoid hammering DB on every question advance.
const remoteWriteTimers = new Map<string, ReturnType<typeof setTimeout>>();
const REMOTE_WRITE_DEBOUNCE_MS = 1500;

function scheduleRemoteWrite(point: LastStudyPoint) {
  if (typeof window === "undefined") return;
  const existing = remoteWriteTimers.get(point.userId);
  if (existing) clearTimeout(existing);
  const timer = setTimeout(() => {
    remoteWriteTimers.delete(point.userId);
    void writeRemote(point);
  }, REMOTE_WRITE_DEBOUNCE_MS);
  remoteWriteTimers.set(point.userId, timer);
}

async function writeRemote(point: LastStudyPoint): Promise<void> {
  try {
    const { error } = await supabase
      .from("user_last_study_point" as any)
      .upsert(
        {
          user_id: point.userId,
          mode: point.mode,
          category: point.category,
          level: point.level,
          current_question: point.currentQuestion,
          question_id: point.questionId,
          route: point.route,
          question_version: point.questionVersion ?? null,
          session_key: point.sessionKey ?? null,
          updated_at: new Date(point.updatedAt).toISOString(),
        },
        { onConflict: "user_id" }
      );
    if (error) console.warn("[lastStudyPoint] remote upsert failed", error);
  } catch (err) {
    console.warn("[lastStudyPoint] remote upsert error", err);
  }
}

async function deleteRemote(userId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from("user_last_study_point" as any)
      .delete()
      .eq("user_id", userId);
    if (error) console.warn("[lastStudyPoint] remote delete failed", error);
  } catch (err) {
    console.warn("[lastStudyPoint] remote delete error", err);
  }
}

export function saveLastStudyPoint(point: Omit<LastStudyPoint, "updatedAt">): void {
  try {
    if (typeof window === "undefined" || !point.userId) return;
    const nextValue: LastStudyPoint = {
      ...point,
      updatedAt: Date.now(),
    };
    window.localStorage.setItem(getStorageKey(point.userId), JSON.stringify(nextValue));
    emitUpdate();
    scheduleRemoteWrite(nextValue);
  } catch (error) {
    console.error("[lastStudyPoint] save failed", error);
  }
}

export function getLastStudyPoint(userId?: string | null): LastStudyPoint | null {
  try {
    if (typeof window === "undefined" || !userId) return null;
    const raw = window.localStorage.getItem(getStorageKey(userId));
    if (!raw) return null;

    const parsed = JSON.parse(raw) as LastStudyPoint;
    if (
      !parsed ||
      parsed.userId !== userId ||
      typeof parsed.route !== "string" ||
      typeof parsed.currentQuestion !== "number" ||
      typeof parsed.updatedAt !== "number"
    ) {
      window.localStorage.removeItem(getStorageKey(userId));
      return null;
    }

    return parsed;
  } catch (error) {
    console.error("[lastStudyPoint] read failed", error);
    if (typeof window !== "undefined" && userId) {
      window.localStorage.removeItem(getStorageKey(userId));
    }
    return null;
  }
}

/**
 * Fetch the latest study point from the database for cross-device sync.
 * Hydrates local cache and emits update event when remote is newer.
 */
export async function fetchRemoteLastStudyPoint(
  userId?: string | null
): Promise<LastStudyPoint | null> {
  try {
    if (typeof window === "undefined" || !userId) return null;
    const { data, error } = await supabase
      .from("user_last_study_point" as any)
      .select("*")
      .eq("user_id", userId)
      .maybeSingle();

    if (error) {
      console.warn("[lastStudyPoint] remote fetch failed", error);
      return null;
    }
    if (!data) return null;

    const row = data as any;
    const remote: LastStudyPoint = {
      userId,
      mode: row.mode,
      category: row.category,
      level: row.level,
      currentQuestion: row.current_question ?? 0,
      questionId: row.question_id,
      route: row.route,
      questionVersion: row.question_version ?? undefined,
      sessionKey: row.session_key ?? undefined,
      updatedAt: row.updated_at ? new Date(row.updated_at).getTime() : Date.now(),
    };

    const local = getLastStudyPoint(userId);
    if (!local || remote.updatedAt > local.updatedAt) {
      window.localStorage.setItem(getStorageKey(userId), JSON.stringify(remote));
      emitUpdate();
    }
    return remote;
  } catch (err) {
    console.warn("[lastStudyPoint] remote fetch error", err);
    return null;
  }
}

export function clearLastStudyPoint(
  userId?: string | null,
  expected?: Partial<Pick<LastStudyPoint, "mode" | "category" | "level" | "route">>
): void {
  try {
    if (typeof window === "undefined" || !userId) return;

    if (expected) {
      const current = getLastStudyPoint(userId);
      if (!current) return;

      const matches = Object.entries(expected).every(([key, value]) => {
        if (value === undefined) return true;
        return current[key as keyof typeof expected] === value;
      });

      if (!matches) return;
    }

    const pendingRemoteWrite = remoteWriteTimers.get(userId);
    if (pendingRemoteWrite) {
      clearTimeout(pendingRemoteWrite);
      remoteWriteTimers.delete(userId);
    }

    window.localStorage.removeItem(getStorageKey(userId));
    emitUpdate();
    void deleteRemote(userId);
  } catch (error) {
    console.error("[lastStudyPoint] clear failed", error);
  }
}
